// ============================================================================
// marketActions.ts — ЗАПИСЬ витрины маркетплейса (заявка/отмена/фото/мастер).
// Вынесено из lib/market.ts (чтение — там): создание идёт через dataService
// (онлайн — сервер с явной ошибкой, офлайн — durable-очередь SyncEngine),
// фото — штатным chunkedUpload с ownerId = id заявки.
// ============================================================================

import { dataService } from '@/lib/dataService';
import { isEffectivelyOffline } from '@/lib/offlineFlag';
import { friendlyError } from '@/lib/errors';
import { uploadFile } from '@/lib/chunkedUpload';
import {
  MARKET_MASTER_TABLE,
  MARKET_REQUEST_TABLE,
  cellsOf,
  listTableRows,
  type CreateMarketRequestInput,
  type CreatedMarketRequest,
  type PhotoUploadResult,
} from '@/lib/market';
import { marketGeoToValues, validateMarketGeo } from '@/lib/marketGeo';

/** Единая запись заявки: онлайн — сервер, офлайн — durable-очередь (SyncEngine). */
export async function createMarketRequest(input: CreateMarketRequestInput): Promise<CreatedMarketRequest> {
  const offlineBefore = isEffectivelyOffline();
  const values: Record<string, unknown> = {
    category_id: input.categoryId,
    address: input.address.trim(),
    descr: input.descr.trim(),
    phone: input.phone.trim(),
    status: 'new',
  };
  if (input.geo) {
    // Проверка гео — до записи: сервер отклонит те же значения, но понятная
    // ошибка в мастере заявки полезнее, чем отказ при сохранении/синке.
    const geoError = validateMarketGeo(input.geo);
    if (geoError) throw new Error(geoError);
    Object.assign(values, marketGeoToValues(input.geo));
  }
  const record = await dataService.create('public', MARKET_REQUEST_TABLE, values);
  const id = String(record.id || '');
  if (!id) throw new Error('Сервер не вернул идентификатор созданной заявки');
  const queued = offlineBefore || isEffectivelyOffline();
  return { id, queued };
}

/** Отмена заявки (штатное изменение статуса; права проверяет сервер). */
export async function cancelMarketRequest(requestId: string): Promise<void> {
  if (!requestId) throw new Error('Не указана заявка');
  await dataService.update('public', MARKET_REQUEST_TABLE, requestId, { status: 'canceled' });
}

/** Загрузка фото к УЖЕ созданной заявке (единый механизм передачи файлов). */
export async function uploadMarketRequestPhotos(
  requestId: string,
  files: File[],
  onProgress?: (done: number, total: number) => void,
): Promise<PhotoUploadResult> {
  const result: PhotoUploadResult = { uploaded: 0, queued: 0, failures: [] };
  if (!requestId || files.length === 0) return result;
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    onProgress?.(i, files.length);
    try {
      const job = await uploadFile(file, { ownerId: requestId });
      if (job.status === 'done' && job.fileId) {
        result.uploaded += 1;
      } else if (job.status === 'queued') {
        result.queued += 1;
      } else {
        result.failures.push({
          fileName: file.name,
          error: job.error || 'не удалось отправить файл',
        });
      }
    } catch (e) {
      result.failures.push({ fileName: file.name, error: friendlyError(e) });
    }
  }
  onProgress?.(files.length, files.length);
  return result;
}

/**
 * Учётная запись выбранного мастера (mkt_masters.entity_id) — чтобы добавить
 * его в обсуждение заявки штатным RPC чата. Пусто — карточка мастера не
 * привязана к пользователю. Ошибка прав пробрасывается (UI сообщает текстом).
 */
export async function loadMarketMasterEntityId(masterId: string): Promise<string> {
  if (!masterId) return '';
  const rows = await listTableRows(MARKET_MASTER_TABLE, {
    pageSize: 1,
    filters: [{ column: 'id', value: masterId }],
  });
  const row = rows[0];
  if (!row) return '';
  return cellsOf(row).get('entity_id') || '';
}

